import {openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isString} from 'ramda-adjunct';

import {useActivateBillingInformationMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError, logError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {DeactivateBillingInformationDialogContent} from '../../components/DeactivateBillingInformationDialogContent/DeactivateBillingInformationDialogContent';

const BILLING_INFORMATION_GRID_CODE = 'billing-information';
const PREDEFINED_COLUMNS = ['billingName', 'companyName'] as const;

export function BillingInformationOverview() {
  const navigate = useNavigate();
  const [activateBillingInformation] = useActivateBillingInformationMutation();

  const handleCreateNewBillingInformation = () => navigate(settingsRoutes.billingInformationNew);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    if (!isString(rowId)) {
      return logError(`Bulk invocation of single-row action ${actionKey} is not implemented`);
    }

    return match(actionKey)
      .with('detail', 'EDIT', () =>
        navigate(composePath(settingsRoutes.billingInformationEdit, {params: {id: rowId}}))
      )
      .with('DEACTIVATE', () => {
        const billingName = getColumnData<string>('billingName')?.value;
        const displayName =
          isString(billingName) && billingName !== ''
            ? billingName
            : (getColumnData<string>('companyName')?.value ?? '');

        openDialog(
          <DeactivateBillingInformationDialogContent
            billingInformationId={rowId}
            billingName={displayName}
            onSuccess={() => refreshData()}
          />,
          {
            id: 'deactivateBillingInformationDialog',
            'data-testid': testIds.settings.billingInformation('deactivateDialog'),
            title: i18n.t('page.settings.labels.deactivateBillingInformationTitle', {
              name: displayName,
            }),
          }
        );
      })
      .with('ACTIVATE', () =>
        activateBillingInformation({billingInformationId: rowId})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyUpdated')))
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('entity.invoice.labels.billingInformationManagement'),
        actions: [
          {
            type: 'button',
            leftIcon: 'content/add_circle',
            title: i18n.t('entity.customer.actions.addBillingInformation'),
            onClick: handleCreateNewBillingInformation,
          },
        ],
      }}
      data-testid="settings-billingInformation"
      description={i18n.t('page.settings.labels.billingInformationDescription')}
    >
      <DmsDataGrid
        gridCode={BILLING_INFORMATION_GRID_CODE}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        data-testid={testIds.settings.billingInformation('datagrid')}
      />
    </SettingsTemplate>
  );
}
